import { PERMISSION, ROLES } from "@/utils/constants";

export class PermissionsService {
  private keycloak: any;

  constructor(keycloak: any) {
    this.keycloak = keycloak;
  }

  hasPermission(rsName: string, scope: string): boolean {
    const permissions = this.keycloak?.permissions_token_parsed?.authorization?.permissions ?? [];
    const permissionRs = permissions.find((p: any) => p.rsname === rsName);
    return permissionRs?.scopes?.includes(scope) ?? false;
  }

  hasRole(roleName: string): boolean {
    const roles = this.keycloak?.tokenParsed?.realm_access?.roles ?? [];
    return roles.includes(roleName);
  }

  // --- Orders ---
  get hasOrderReadPermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.read);
  }

  get hasOrderReadAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.readAll);
  }

  get hasOrderReadCountryPermission(): boolean {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.readCountry)
  }

  get hasOrderCreatePermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.create);
  }

  get hasOrderCreateAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.createAll);
  }

  get hasOrderUpdatePermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.update);
  }

  get hasOrderUpdateAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.updateAll);
  }

  get hasOrderDeletePermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.delete);
  }

  get hasOrderDeleteAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.order, PERMISSION.scopes.deleteAll);
  }

  // --- Order Reports ---
  get hasOrderReportReadAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.orderReport, PERMISSION.scopes.readAll);
  }

  get hasOrderReportCreateAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.orderReport, PERMISSION.scopes.createAll);
  }

  get hasOrderReportDeleteAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.orderReport, PERMISSION.scopes.deleteAll);
  }

  // --- Order Supporting Files ---
  get hasOrderSupportingFileReadAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.orderSupportingFile, PERMISSION.scopes.readAll);
  }

  // --- Patients ---
  get hasPatientReadPermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.read);
  }

  get hasPatientReadAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.readAll);
  }

  get hasPatientReadCountryPermission(): boolean {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.readCountry)
  }

  get hasPatientCreatePermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.create);
  }

  get hasPatientCreateAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.createAll);
  }

  get hasPatientUpdatePermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.update);
  }

  get hasPatientUpdateAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.updateAll);
  }

  get hasPatientDeletePermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.delete);
  }

  get hasPatientDeleteAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.patient, PERMISSION.scopes.deleteAll);
  }

  // --- Notifications ---
  get hasNotificationReadPermission() {
    return this.hasPermission(PERMISSION.rsNames.notification, PERMISSION.scopes.read);
  }

  get hasNotificationReadAllPermission() {
    return this.hasPermission(PERMISSION.rsNames.notification, PERMISSION.scopes.readAll);
  }

  // --- Roles ---
  get hasRoleExternalUser() {
    return this.hasRole(ROLES.externalUser);
  }

  get hasRoleExternalSales() {
    return this.hasRole(ROLES.externalSales);
  }

  get hasRoleInternalSales() {
    return this.hasRole(ROLES.internalSales);
  }

  get hasRoleCegatVerified() {
    return this.hasRole(ROLES.cegatVerified);
  }

  get hasRoleReportRelease() {
    return this.hasRole(ROLES.reportRelease);
  }

  get canEditReportComment() {
    return this.hasRoleInternalSales;
  }
}
